import React, { useState } from "react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionCard, StatusBadge, SignatureBadge, ProvenanceBadge, Mono, InfoNote } from "@/components/uikit";
import { runHct3, HCT3_DEFAULTS } from "@/lib/hct3";
import { scoreAnomaly } from "@/lib/anomaly";
import { verifyRecord, computeHash } from "@/lib/crypto";
import { verifySignature } from "@/lib/signing";
import { generateEvidencePack } from "@/lib/evidencePack";
import { toast } from "sonner";

const STEPS = [
  ["Capture", "FileInput"],
  ["HCT-3 Analyze", "Activity"],
  ["ADA-4WM Assess", "LayoutGrid"],
  ["K-Crypto Hash", "Hash"],
  ["Sign", "PenTool"],
  ["Anchor", "Anchor"],
  ["Store", "Database"],
  ["Verify", "ShieldCheck"],
  ["Export", "FileText"],
];

export default function Workflow() {
  const { runWorkflow, identity, settings, records, integrity, sigStatus } = useApp();
  const [payload, setPayload] = useState(
    JSON.stringify({ mission: "4WM-alpha", operator: "console-user", context: "integrated evidence cycle" }, null, 2)
  );
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    setDone(0);
    try {
      // 1. Capture + 2. HCT-3 analyze
      const telemetry = { ...HCT3_DEFAULTS };
      const hct = runHct3(telemetry, settings.thresholds);
      setDone(2);
      // 3. ADA-4WM assess
      const anomaly = scoreAnomaly(
        {
          baselineDifference: hct.cycle2.baseline_difference,
          temporalDelta: telemetry.temporal_delta,
          driftMagnitude: telemetry.raw_drift - telemetry.corrected_offset,
          integrityBroken: false,
        },
        settings.thresholds
      );
      const ada = { summary: `4 mirrors mapped · convergence ${hct.cycle3.convergence_pct}%`, layer: 21 };
      setDone(3);
      // Compose the SINGLE evidence object (analysis embedded → hashed & tamper-evident)
      const composed = JSON.parse(payload && payload.trim().startsWith("{") ? payload : "{}");
      const fullPayload = JSON.stringify(
        { capture: composed, hct3: { telemetry, result: hct.cycle3, indicators: hct.indicators }, ada, anomaly: { score: anomaly.score, classification: anomaly.classification } },
        null,
        2
      );
      // 4-7. Hash + Sign + Anchor + Store (single record via runWorkflow)
      const rec = await runWorkflow({
        payload: fullPayload,
        payload_type: "Telemetry Object",
        layer: 21,
        notes: "Integrated cross-module workflow (SIMULATED telemetry embedded)",
        drift_status: hct.indicators.anomaly_threshold === "EXCEEDED" ? "DRIFT" : hct.indicators.l8_drift_trigger ? "WARNING" : "NONE",
        source_tag: "simulated",
        telemetry,
        hash_mode: "canonical",
        meta: { hct3: hct.cycle3, ada, anomaly: { score: anomaly.score, classification: anomaly.classification } },
      });
      setDone(7);
      // 8. Verify
      const cur = await computeHash(rec);
      const hashV = await verifyRecord(rec);
      const sigV = await verifySignature(rec.signature, cur.hash);
      setDone(8);
      setResult({ rec, hashOk: hashV.ok, sig: sigV.state, anomaly });
      setDone(9);
      toast.success("Workflow complete", { description: `${rec.event_id} anchored & verified` });
    } catch (e) {
      toast.error("Workflow failed", { description: String(e && e.message ? e.message : e) });
    } finally {
      setBusy(false);
    }
  };

  const exportPack = () => {
    if (!result) return;
    const packId = generateEvidencePack({
      records: [result.rec],
      integrity: [{ selfOk: result.hashOk, linkOk: true }],
      sigStatus: { [result.rec.id]: result.sig },
      scopeLabel: "Single record (Full Workflow output)",
    });
    toast.success("Evidence Pack generated", { description: packId });
  };

  return (
    <div>
      <PageHeader overline="Integrated Evidence Cycle" title="Full Workflow" icon="Workflow"
        subtitle="One click: Capture → HCT-3 Analyze → ADA-4WM Assess → K-Crypto Hash → Sign → Anchor → Store → Verify → Export. A single evidence object flows end-to-end."
        actions={<ProvenanceBadge source="simulated" />} />

      <InfoNote tone="warn" className="mb-6">
        This workflow embeds <strong>SIMULATED HCT-3 telemetry — NOT external sensor data</strong>. The resulting
        record is LOCAL cryptographic evidence.
      </InfoNote>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard title="Capture Context" icon="FileInput" testId="workflow-input-card">
          <Label htmlFor="wf-payload" className="mb-1.5 block text-slate-300">Capture payload (JSON)</Label>
          <Textarea id="wf-payload" data-testid="workflow-payload-input" rows={7} value={payload} onChange={(e) => setPayload(e.target.value)}
            className="border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200" />
          <Button onClick={run} disabled={busy} className="mt-3 w-full bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400 glow-cyan" data-testid="workflow-run-button">
            {busy ? <Icons.Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icons.Play className="mr-2 h-4 w-4" />} Run Full Workflow
          </Button>
          <p className="mt-2 text-xs text-slate-500">
            Signing identity: {identity ? <Mono>{identity.public_key_id}</Mono> : <span className="text-amber-400">none (records will be UNSIGNED)</span>}
          </p>
        </SectionCard>

        <SectionCard title="Pipeline" icon="GitBranch" className="lg:col-span-2" testId="workflow-pipeline-card">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {STEPS.map(([label, icon], i) => {
              const Icon = Icons[icon];
              const active = done >= i + 1;
              return (
                <div key={label} data-testid={`workflow-step-${i}`}
                  className={`flex items-center gap-2 rounded-md border p-2.5 ${active ? "border-cyan-600 bg-cyan-950/30 text-cyan-200" : "border-slate-800 bg-slate-950/50 text-slate-500"}`}>
                  <span className={`flex h-7 w-7 items-center justify-center rounded ${active ? "bg-cyan-900 text-cyan-200" : "bg-slate-800 text-slate-500"}`}>
                    {active ? <Icons.Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
                </div>
              );
            })}
          </div>

          {result && (
            <div className="mt-4 space-y-2" data-testid="workflow-result">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={result.hashOk ? "VERIFIED" : "WARNING"} />
                <SignatureBadge state={result.sig} testId="workflow-signature-badge" />
                <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">anomaly: {result.anomaly.classification}</span>
              </div>
              <Field label="event_id" value={result.rec.event_id} />
              <Field label="sha256" value={result.rec.sha256} />
              <Field label="previous_hash" value={result.rec.previous_hash} />
              <Button onClick={exportPack} variant="outline" className="mt-1 border-cyan-800 text-cyan-300 hover:bg-cyan-950/40" data-testid="workflow-export-button">
                <Icons.FileText className="mr-2 h-4 w-4" /> Export Evidence Pack (PDF)
              </Button>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-950/60 px-3 py-1.5">
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="break-all font-mono text-xs text-cyan-300">{value}</p>
    </div>
  );
}
